import { useEffect, useState } from "react";

import { localTimezone, type ScheduleMode, type Trigger } from "@/lib/jobs";

import { DAY_INITIALS, MODES, modeDescription } from "./schedule";

/**
 * The schedule editor — `5e` expanded, and the Schedule card on `6j2`.
 *
 * **Four radio modes, not a cron string**, and only the selected mode reveals controls. A cron
 * field would be smaller and would put the burden of remembering its syntax on the one person who
 * uses this, once a quarter.
 *
 * Saving stamps the browser's own IANA zone onto the trigger. "03:00 local" is meaningless on a
 * UTC container, and a fixed offset would drift an hour twice a year.
 */
export function ScheduleEditor({
  trigger,
  onSave,
  onError,
  blocked = false,
  sharingTime = 0,
  phone = false,
}: {
  trigger: Trigger;
  onSave: (trigger: Trigger) => Promise<unknown> | void;
  onError?: (error: unknown) => void;
  /** This job's time and priority collide with another's, so neither runs on schedule. Reported by
   *  the server, so the editor and the row cannot disagree about it. */
  blocked?: boolean;
  /** How many OTHER scheduled jobs share this time of day — the hint that makes the priority field
   *  mean something before you have collided with anything. */
  sharingTime?: number;
  phone?: boolean;
}) {
  // Local draft so a half-typed time is not sent on every keystroke. Keyed on the job by the
  // parent, so switching rows starts from that row's saved schedule.
  const [draft, setDraft] = useState<Trigger>(trigger);

  // Adopt the stored trigger when its VALUE changes — the server normalises what it saves
  // (`9:5` → `09:05`, days sorted), and the editor should show what was actually stored.
  //
  // Keyed on the serialised value, not on `trigger`'s identity: the job list is replaced on every
  // SSE progress frame, so an identity-keyed effect would reset the field under your cursor
  // several times a second during a run.
  const stored = JSON.stringify(trigger);
  useEffect(() => setDraft(JSON.parse(stored) as Trigger), [stored]);

  const update = (patch: Partial<Trigger>) => {
    const next = { ...draft, ...patch, timezone: localTimezone() };
    setDraft(next);
    // Every change saves. There is no Save button in the design, and a schedule editor whose
    // state you can lose by collapsing the row is worse than one extra request per click.
    //
    // A REJECTED save snaps back. Without this the editor sits showing a schedule the server
    // refused while the row's own pill shows the stored one — two answers to "when does this run",
    // and the wrong one is the bigger.
    Promise.resolve(onSave(next)).catch((e) => {
      setDraft(JSON.parse(stored) as Trigger);
      onError?.(e);
    });
  };

  const setMode = (mode: ScheduleMode) => {
    // Give the new mode a usable value rather than saving one the server will reject.
    const patch: Partial<Trigger> = { mode };
    if (mode === "interval" && !draft.everyNDays) patch.everyNDays = 7;
    if (mode === "daysOfWeek" && draft.days.length === 0) patch.days = [0];
    if (mode === "dayOfMonth" && !draft.dayOfMonth) patch.dayOfMonth = 1;
    update(patch);
  };

  const toggleDay = (day: number) => {
    const days = draft.days.includes(day)
      ? draft.days.filter((d) => d !== day)
      : [...draft.days, day].sort((a, b) => a - b);
    // At least one day, always: an empty set is a schedule that never fires, which the panel would
    // draw as scheduled. The server refuses it too.
    if (days.length === 0) return;
    update({ days });
  };

  const parts: Parts = { draft, setMode, toggleDay, update, blocked, sharingTime };
  return phone ? <PhoneEditor {...parts} /> : <DesktopEditor {...parts} />;
}

interface Parts {
  draft: Trigger;
  setMode: (mode: ScheduleMode) => void;
  toggleDay: (day: number) => void;
  update: (patch: Partial<Trigger>) => void;
  blocked: boolean;
  sharingTime: number;
}

const MISSED_NOTE = "ローカル時刻 · 停止中に逃した実行は次回起動時に走ります";

/** Mirrors the server's bounds (`jobs/model.py`). */
export const PRIORITY_MIN = 1;
export const PRIORITY_MAX = 999;

const PRIORITY_HINT = "小さい数字が先に走ります。時刻が違うジョブには影響しません。";
const CONFLICT_NOTE =
  "この時刻のこの優先度は別のジョブが使っています。優先度が違うまで、どちらも予定どおりには走りません。";

function clampPriority(raw: string): number {
  return Math.min(PRIORITY_MAX, Math.max(PRIORITY_MIN, Number(raw) || PRIORITY_MIN));
}

/** `Two other jobs also run at 03:00.` — empty when this job has the slot to itself. */
function sharingNote(count: number, time: string): string {
  if (count <= 0) return "";
  return `${time} には他に${count}件のジョブが走ります。`;
}

/**
 * The priority field, shared by both editors.
 *
 * **A conflicting value still saves.** The server accepts it and reports `blocked`, which is what
 * the red border and the note below are drawn from. Refusing it would make it impossible to type
 * *through* a neighbour's number on the way to a free one, and `update`'s snap-back would fight
 * every keystroke on the way.
 */
function PriorityField({
  value,
  blocked,
  onChange,
  phone = false,
}: {
  value: number;
  blocked: boolean;
  onChange: (priority: number) => void;
  phone?: boolean;
}) {
  return (
    <input
      type="number"
      min={PRIORITY_MIN}
      max={PRIORITY_MAX}
      value={value}
      aria-label="優先度"
      aria-invalid={blocked}
      onChange={(e) => onChange(clampPriority(e.target.value))}
      className={[
        "rounded-[var(--r-2)] border bg-panel text-center font-mono text-ink",
        phone ? "min-h-[44px] w-[86px] rounded-[var(--r-3)] px-3 text-[17px]/[1]" : "w-[62px] px-2.5 py-[7px] text-[15px]/[1]",
        blocked ? "border-[1.5px] border-accent text-accent-text" : "border-line-strong",
      ].join(" ")}
    />
  );
}

/** The red alert under the priority row, when this job's number collides with another's. */
function ConflictNote({ phone = false }: { phone?: boolean }) {
  return (
    <p
      className={[
        "rounded-[var(--r-3)] border border-accent-border bg-accent-ground px-3 py-[9px] text-secondary text-accent-text",
        phone ? "" : "ml-[90px]",
      ].join(" ")}
    >
      {CONFLICT_NOTE}
    </p>
  );
}

/** Mirrors the server's ceiling (`jobs/model.py:MAX_INTERVAL_DAYS`). Past a year an interval is
 *  not a schedule, and an unbounded one overflows the date arithmetic behind the `Next` column. */
export const MAX_INTERVAL_DAYS = 365;

function clampInterval(raw: string): number {
  return Math.min(MAX_INTERVAL_DAYS, Math.max(1, Number(raw) || 1));
}

function DesktopEditor({ draft, setMode, toggleDay, update, blocked, sharingTime }: Parts) {
  return (
    <div className="flex flex-col gap-3 py-0.5 pr-4 pb-4 pl-9">
      <Row label="スケジュール">
        <div className="inline-flex gap-0.5 rounded-[var(--r-3)] bg-sunken p-0.5">
          {MODES.map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              onClick={() => setMode(mode)}
              className={[
                "rounded-[var(--r-2)] px-[11px] py-[7px] text-[14px]/[1] font-semibold",
                mode === draft.mode
                  ? "bg-panel text-ink shadow-[var(--sh-inset)]"
                  : "text-ink-3 hover:text-ink-2",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      </Row>

      {draft.mode !== "manual" && (
        <Row label="実行日">
          {draft.mode === "daysOfWeek" && (
            <div className="flex gap-[5px]">
              {DAY_INITIALS.map((initial, day) => (
                <DayToggle
                  key={day}
                  day={day}
                  initial={initial}
                  on={draft.days.includes(day)}
                  onClick={() => toggleDay(day)}
                />
              ))}
            </div>
          )}
          {draft.mode === "interval" && (
            <label className="flex items-center gap-2 text-secondary text-ink-3">
              N日ごと
              <input
                type="number"
                min={1}
                max={MAX_INTERVAL_DAYS}
                value={draft.everyNDays ?? 7}
                onChange={(e) => update({ everyNDays: clampInterval(e.target.value) })}
                className="w-[64px] rounded-[var(--r-2)] border border-line-strong bg-panel px-2.5 py-[7px] font-mono text-[15px]/[1] text-ink"
              />
              日
            </label>
          )}
          {draft.mode === "dayOfMonth" && (
            <label className="flex items-center gap-2 text-secondary text-ink-3">
              毎月
              <input
                type="number"
                min={1}
                max={31}
                value={draft.dayOfMonth ?? 1}
                onChange={(e) =>
                  update({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })
                }
                className="w-[64px] rounded-[var(--r-2)] border border-line-strong bg-panel px-2.5 py-[7px] font-mono text-[15px]/[1] text-ink"
              />
            </label>
          )}
          <span className="text-secondary text-ink-3">時刻</span>
          <TimeField value={draft.time} onChange={(time) => update({ time })} />
          <span className="text-meta text-ink-4">
            {sharingNote(sharingTime, draft.time) || MISSED_NOTE}
          </span>
        </Row>
      )}

      {/* Offered for a scheduled job only. A manual job carries a priority too — it decides where a
          Run-now lands in the queue — but that is a tie-break between things you pressed yourself
          seconds apart, and a control here would invite tuning something nothing asks you to think
          about. */}
      {draft.mode !== "manual" && (
        <>
          <Row label="優先度">
            <PriorityField
              value={draft.priority}
              blocked={blocked}
              onChange={(priority) => update({ priority })}
            />
            <span className="text-meta text-ink-4">{PRIORITY_HINT}</span>
          </Row>
          {blocked && <ConflictNote />}
        </>
      )}

      <Row label="">
        <span className="text-secondary text-ink-3">失敗時に通知</span>
        <Switch
          on={draft.notifyOnFailure}
          onClick={() => update({ notifyOnFailure: !draft.notifyOnFailure })}
          label="失敗時に通知"
        />
      </Row>
    </div>
  );
}

function PhoneEditor({ draft, setMode, toggleDay, update, blocked, sharingTime }: Parts) {
  return (
    <div className="flex flex-col">
      {MODES.map(({ mode, label }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setMode(mode)}
          className={[
            "flex min-h-[52px] items-center gap-[11px] border-b border-line-soft px-[13px] py-[11px] text-left",
            mode === draft.mode ? "bg-panel" : "",
          ].join(" ")}
        >
          <span
            className={[
              "size-[18px] shrink-0 rounded-full border",
              mode === draft.mode ? "border-[5px] border-accent" : "border-line-strong",
            ].join(" ")}
          />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span
              className={[
                "text-[16px]/[1.3]",
                mode === draft.mode ? "font-semibold text-ink" : "text-ink-2",
              ].join(" ")}
            >
              {label}
            </span>
            <span className="text-meta text-ink-3">{modeDescription(mode, draft)}</span>
          </span>
        </button>
      ))}

      {draft.mode !== "manual" && (
        <div className="flex flex-col gap-[9px] bg-panel px-[13px] py-[11px]">
          {draft.mode === "daysOfWeek" && (
            <div className="flex gap-[5px]">
              {DAY_INITIALS.map((initial, day) => (
                <DayToggle
                  key={day}
                  day={day}
                  initial={initial}
                  on={draft.days.includes(day)}
                  onClick={() => toggleDay(day)}
                  phone
                />
              ))}
            </div>
          )}
          {draft.mode === "interval" && (
            <label className="flex items-center gap-2.5 text-secondary text-ink-3">
              N日ごと
              <input
                type="number"
                min={1}
                max={MAX_INTERVAL_DAYS}
                value={draft.everyNDays ?? 7}
                onChange={(e) => update({ everyNDays: clampInterval(e.target.value) })}
                className="min-h-[44px] w-[80px] rounded-[var(--r-3)] border border-line-strong bg-panel px-3 font-mono text-[17px]/[1] text-ink"
              />
              日
            </label>
          )}
          {draft.mode === "dayOfMonth" && (
            <label className="flex items-center gap-2.5 text-secondary text-ink-3">
              毎月
              <input
                type="number"
                min={1}
                max={31}
                value={draft.dayOfMonth ?? 1}
                onChange={(e) =>
                  update({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })
                }
                className="min-h-[44px] w-[80px] rounded-[var(--r-3)] border border-line-strong bg-panel px-3 font-mono text-[17px]/[1] text-ink"
              />
            </label>
          )}
          <div className="flex items-center gap-2.5">
            <span className="text-secondary text-ink-3">時刻</span>
            <TimeField value={draft.time} onChange={(time) => update({ time })} phone />
            <span className="flex-1 text-meta text-ink-4">
              {sharingNote(sharingTime, draft.time) ||
                "停止中に逃した実行は次回起動時に走ります"}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-secondary text-ink-3">順序</span>
            <PriorityField
              value={draft.priority}
              blocked={blocked}
              onChange={(priority) => update({ priority })}
              phone
            />
            <span className="flex-1 text-meta text-ink-4">{PRIORITY_HINT}</span>
          </div>
          {blocked && <ConflictNote phone />}
        </div>
      )}
    </div>
  );
}

/** The label column the expanded job row is built from — `SCHEDULE`, and now `SETTINGS`.
 *
 * Exported so the settings editor lines up with the schedule editor exactly. Two label columns of
 * slightly different widths in one expanded row is the kind of thing nobody can name and everybody
 * sees. */
export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="w-[78px] shrink-0 text-field-label font-semibold uppercase tracking-[.05em] text-ink-4">
        {label}
      </span>
      {children}
    </div>
  );
}

function DayToggle({
  day,
  initial,
  on,
  onClick,
  phone = false,
}: {
  day: number;
  initial: string;
  on: boolean;
  onClick: () => void;
  phone?: boolean;
}) {
  const names = ["日曜", "月曜", "火曜", "水曜", "木曜", "金曜", "土曜"];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-label={names[day]}
      className={[
        "rounded-[var(--r-3)] border text-[14px]/[1] font-semibold",
        // 30px on desktop; on a phone they stretch to fill the row and clear the 44px target.
        phone ? "min-h-[44px] flex-1" : "size-[30px]",
        on
          ? "border-transparent bg-accent text-white"
          : "border-line-strong bg-panel text-ink-3",
      ].join(" ")}
    >
      {initial}
    </button>
  );
}

/**
 * A plain `HH:MM` field, not `<input type="time">`.
 *
 * The native control renders in the browser's locale, which here meant `03:00 AM` with a picker
 * icon — a 12-hour clock in an app that is 24-hour everywhere, and wider than the cell the design
 * gives it. Read's gutter already taught this codebase what a 12-hour clock costs.
 */
function TimeField({
  value,
  onChange,
  phone = false,
}: {
  value: string;
  onChange: (value: string) => void;
  phone?: boolean;
}) {
  const [draft, setDraft] = useState(value);

  // Follow the trigger when it changes underneath — switching modes keeps the same field mounted.
  useEffect(() => setDraft(value), [value]);

  const commit = (text: string) => {
    const match = /^(\d{1,2}):?(\d{2})$/.exec(text.trim());
    if (!match) {
      setDraft(value); // unparseable: snap back rather than saving something invented
      return;
    }
    const hour = Math.min(23, Number(match[1]));
    const minute = Math.min(59, Number(match[2]));
    const normalised = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    setDraft(normalised);
    if (normalised !== value) onChange(normalised);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={draft}
      aria-label="時刻"
      placeholder="HH:MM"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && commit((e.target as HTMLInputElement).value)}
      className={[
        "rounded-[var(--r-2)] border border-line-strong bg-panel text-center font-mono text-ink",
        phone
          ? "min-h-[44px] w-[86px] rounded-[var(--r-3)] px-3 text-[17px]/[1]"
          : "w-[62px] px-2.5 py-[7px] text-[15px]/[1]",
      ].join(" ")}
    />
  );
}

export function Switch({
  on,
  onClick,
  label,
  phone = false,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  phone?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={[
        "relative shrink-0 rounded-full transition-colors",
        phone ? "h-[26px] w-[44px]" : "h-[24px] w-[40px]",
        on ? "bg-ink-3" : "bg-line-strong",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-[3px] rounded-full bg-white transition-all",
          phone ? "size-[20px]" : "size-[18px]",
          on ? "right-[3px]" : "left-[3px]",
        ].join(" ")}
      />
    </button>
  );
}
